import { SubstitutionReportType } from '@bp/planung-graphql-types';
import { AddIcon, Button, ButtonGroup, DeleteIcon, ImportIcon, Table, TableColumns } from '@bp/ui-components';
import dayjs, { Dayjs } from 'dayjs';
import { observer } from 'mobx-react-lite';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useConfirm } from '../../../hooks/useConfirm';
import { useSelectedSchoolYear } from '../../../hooks/useSelectedSchoolYear';
import { useSubstitutionReportQuery } from '../../../types/planung-graphql-client-defs';
import {
  createSubstitutionStatsExcel,
  createSubstitutionStatsMonthly,
  createSubstitutionStatsTotal,
  deleteSubstitutionStats,
  downloadSubstitutionStats,
} from '../../../utils/substitutionReport';

type SubstitutionStatisticReportsTableType = {
  uuid: string;
  name: string;
  type: SubstitutionReportType;
  timestamp: Dayjs;
};

type SubstitutionStatisticReportsTableProps = {
  subType: SubstitutionReportType;
};

export const SubstitutionStatisticReportsTable = observer(({ subType }: SubstitutionStatisticReportsTableProps) => {
  const { t } = useTranslation();
  const schoolYear = useSelectedSchoolYear();

  const [loading, setLoading] = useState<string | 'all' | null>(null);

  const { ConfirmationDialog, confirm } = useConfirm({
    defaultMessage: t('delete.message', { context: 'female', count: 1, type: t('pdf.title', { count: 1 }) }),
  });

  const [{ data: substitutionReportsData }, refetchReports] = useSubstitutionReportQuery({
    variables: { schoolYearUuid: schoolYear?.uuid ?? '', type: subType },
    pause: !schoolYear,
    // Berichte entstehen über die PDF- und Excel-Routen; davon erfährt der Cache nichts.
    requestPolicy: 'cache-and-network',
  });

  // Stabile Referenzen: die Tabelle setzt bei neuen data/columns ihren Zustand zurück.
  const columns: TableColumns<SubstitutionStatisticReportsTableType>[] = useMemo(
    () => [
      {
        header: '',
        id: 'name',
        accessorKey: 'name',
        size: 480,
      },
      {
        header: '',
        id: 'timestamp',
        accessorFn: (row) => {
          return row.timestamp.format('DD.MM.YY HH:mm');
        },
        size: 120,
      },
    ],
    [],
  );

  const data: SubstitutionStatisticReportsTableType[] = useMemo(
    () =>
      substitutionReportsData?.substitutionReports.map((report) => {
        return {
          uuid: report.uuid,
          name: report.title,
          type: report.type,
          timestamp: dayjs(report.jobFinished),
        };
      }) ?? [],
    [substitutionReportsData?.substitutionReports],
  );

  async function handleDownload(report: SubstitutionStatisticReportsTableType) {
    if (loading) return;
    setLoading(report.uuid);
    await downloadSubstitutionStats(report.uuid, report.type);
    setLoading(null);
  }

  async function handleDelete(report: SubstitutionStatisticReportsTableType) {
    if (loading) return;
    const result = await confirm();
    if (!result) return;
    setLoading(report.uuid);
    await deleteSubstitutionStats(report.uuid, report.type);
    refetchReports({ requestPolicy: 'network-only' });
    setLoading(null);
  }

  async function handleGenerate() {
    if (loading) return;
    setLoading('all');
    if (subType === SubstitutionReportType.StatisticTotal) {
      await createSubstitutionStatsTotal(schoolYear?.uuid ?? '');
    } else if (subType === SubstitutionReportType.StatisticMonth) {
      await createSubstitutionStatsMonthly(schoolYear?.uuid ?? '');
    } else if (subType === SubstitutionReportType.StatisticExcel) {
      await createSubstitutionStatsExcel(schoolYear?.uuid ?? '');
    }
    refetchReports({ requestPolicy: 'network-only' });
    setLoading(null);
  }

  return (
    <>
      <Table<SubstitutionStatisticReportsTableType>
        data={data}
        columns={columns}
        hideHeader
        showActionBar
        actionBarSettings={{
          globalFilterPlaceholder: t('common.search'),
          extendedActionsRight: (
            <Button isLoading={loading === 'all'} icon={<AddIcon />} onClick={handleGenerate}>
              {t('common.generate')}
            </Button>
          ),
        }}
        emptyStateSettings={{ size: 'small', hideIcon: true, subtitle: '', title: t('pdf.noPdfFound') }}
        customPadding='var(--spacing-6)'
        lastColWidth='69px'
        lastCol={({ original }) => (
          <ButtonGroup loading={loading === original.uuid}>
            <Button
              hierarchy='secondary'
              icon={<ImportIcon className='small' />}
              onClick={() => handleDownload(original)}
            />
            <Button
              hierarchy='secondary'
              icon={<DeleteIcon className='small' />}
              onClick={() => handleDelete(original)}
            />
          </ButtonGroup>
        )}
      />

      <ConfirmationDialog />
    </>
  );
});
